import { hooks, state } from "@springtree/eva-sdk-react-recoil";
import { Core } from "@springtree/eva-services-core";
import classNames from "classnames";
import { Entry } from "contentful";
import { useRouter } from "next/router";
import { ButtonHTMLAttributes, DetailedHTMLProps, useEffect, useRef, useState } from "react";

import Button, { ButtonVariants, ServiceButtonVariants } from "~/components/common/button";
import EscapeNewLine from "~/components/common/escape-new-line";
import { useCartNotification } from "~/contexts/cart-notification";
import { useConfiguration, useObjectConfigurationValue } from "~/contexts/configuration";
import { useCart } from "~/hooks/use-cart";
import { useGlobalNotification } from "~/hooks/use-global-notification";
import useScript from "~/hooks/use-script";
import Logger from "~/services/logger/logger";
import Editorials from "~/types/editorials";
import CommonUtils from "~/utils/common-utils";
import contentfulUtils from "~/utils/contentful-utils";
import { assertEditorialType } from "~/utils/editorial-utils";
import TrackingUtils from "~/utils/tracking-utils";

import styles from "./revieve-modal-trigger.module.scss";

type Props = DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> & {
  entry?: Entry<unknown>;
  revieveType?: "PDPVirtualTryOn" | "foundationFinder" | "skincare" | "virtualTryOn";
  buttonText?: string;
  className?: string;
  variant?: ButtonVariants["variant"] | ServiceButtonVariants["variant"];
  productId?: number | string;
  renderToId?: string;
  onOpen?: () => void;
  onClose?: () => void;
};

declare global {
  var Revieve: any;
}

export default function RevieveModalTrigger(props: Props) {
  const [active, setActive] = useState(false);
  const [isRestarted, setIsRestarted] = useState(false);
  const [hasProductError, setHasProductError] = useState(false);

  const inspectorMode = contentfulUtils.useInspectorMode(props.entry);

  useScript(process.env.NEXT_PUBLIC_REVIEVE_API_URL);

  const { eva } = useConfiguration();
  const shoppingCartAdd = hooks.useShoppingCartAdd({ service: Core.AddProductToOrder });
  const shoppingCartModify = hooks.useCallService({ service: Core.AddProductToOrder });
  const refreshCart = hooks.useSetServiceStale({ serviceState: state.checkout.shoppingCartState });
  const { notifyAdded, resetNotifications } = useCartNotification();
  const { newErrorGlobalNotification, closeGlobalNotification } = useGlobalNotification();
  const cart = useCart();
  const cartIdRef = useRef(cart?.ShoppingCart.ID);
  const revieveConfigAppSetting = useObjectConfigurationValue("App:RevieveConfigs");

  const router = useRouter();

  function revieveChangeShade(productId?: number | string): Promise<void> {
    if (!productId) {
      return Promise.reject("Revieve: No product provided");
    }
    Revieve.API.liveAR.resetTryOnProducts();
    return Revieve.API.liveAR.addTryOnProduct(productId) as Promise<void>;
  }

  function unmount() {
    if (typeof Revieve !== "undefined" && Revieve?.API) {
      setTimeout(() => {
        Revieve.API.unmount();
        doOnTarget((e) => e.classList.remove("show"));
        props.onClose?.();
        setActive(false);
        document.body.style.removeProperty("overflow");
      }, Number(process.env.NEXT_PUBLIC_REVIEVE_UNMOUNT_TIME) || 500);
    }
  }

  // Unmount Revieve when exiting PDP
  function onRouteChange(_url: string, { shallow }: { shallow: boolean }) {
    setHasProductError(false);
    closeGlobalNotification();
    if (typeof shallow != "undefined" && !shallow) {
      unmount();
    }
  }

  function doOnTarget(callback?: (container: HTMLElement) => void) {
    const el = typeof window !== "undefined" && props.renderToId && document.getElementById(props.renderToId);
    if (callback && el) {
      callback(el);
    }
    return el;
  }

  function ProductNotValid(errorMessage: any, productId?: number | string) {
    Logger.instance.warn(`Product ${productId} not valid on Revieve:`, errorMessage);
    unmount();
    setHasProductError(true);
    newErrorGlobalNotification({
      slug: "revieveGenericError",
      closeOnClickCta: true,
      ctaCaption: "generic.cancel",
    });
    resetNotifications();
  }

  function onTriggerClick(productId?: number | string) {
    if (typeof Revieve === "undefined") {
      return;
    }

    if (!active) {
      // reinit to override previous config
      Logger.instance.info("revieve partner_id: ", revieveConfig.partner_id);
      setActive(true);
      Revieve.Init(revieveConfig, () => {
        Logger.instance.info(`[Revieve] Loaded Revieve integration`);
        if (productId) {
          revieveChangeShade(productId)
            .then((_) => {
              doOnTarget((e) => e.classList.add("show"));
            })
            .catch((e) => {
              ProductNotValid(e, productId);
            });
        }
        props.onOpen?.();
        doOnTarget((e) => e.classList.add("show"));
        Revieve.API.show();
      });
    }

    if (!productId) {
      Revieve.API.liveAR.resetTryOnProducts();
      if (revieveCustomId) {
        Revieve.API.liveAR.addTryOnProduct(revieveCustomId).catch((e: any) => ProductNotValid(e, revieveCustomId));
      }
    }
  }

  useEffect(() => {
    if (cartIdRef.current !== cart?.ShoppingCart.ID) {
      cartIdRef.current = cart?.ShoppingCart.ID;
    }
  }, [cart?.ShoppingCart.ID]);

  // update applied shade on productId change
  useEffect(() => {
    if (typeof Revieve !== "undefined" && typeof props.productId !== "undefined") {
      setIsRestarted(false);
      if (props.hidden) {
        unmount();
      } else {
        revieveChangeShade(props.productId)
          .then(() => {
            props.onOpen?.();
            doOnTarget((e) => e.classList.add("show"));
          })
          .catch((e) => {
            ProductNotValid(e, props.productId);
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.productId, isRestarted]);

  useEffect(() => {
    router.events && router.events.on("routeChangeStart", onRouteChange);
    return () => {
      router.events && router.events.off("routeChangeStart", onRouteChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.asPath]);

  if (!revieveConfigAppSetting) {
    return null;
  }

  let partnerId = revieveConfigAppSetting[props.revieveType ?? "PDPVirtualTryOn"];
  let buttonText = props.buttonText;
  let revieveCustomId: undefined | string = undefined;
  if (props.entry) {
    assertEditorialType<Editorials.RevieveModalTrigger>(props.entry, "revieveModalTrigger");
    partnerId = revieveConfigAppSetting[props.entry.fields.revieveType];
    buttonText = props.entry.fields.buttonText;
    revieveCustomId = props.entry.fields.revieveCustomId;
  }

  // Return empty component, keep all the logic
  if (props.hidden) {
    return <></>;
  }

  const revieveConfig = (() => {
    const lang = router.locale?.split("-")[0] ?? "en";
    return {
      partner_id: partnerId,
      locale: lang,
      env: CommonUtils.parseBoolean(process.env.NEXT_PUBLIC_REVIEVE_ENABLE_PROD_ENVIRONMENT) ? "prod" : "test",
      renderTo: doOnTarget(),
      onClose: () => {
        unmount();
      },
      onAddToCart: async (products: { id: string }[] | { id: string; bundle?: { products: { id: string }[] } }) => {
        Logger.instance.debug("Revieve addToCart callback", products);
        const addedAt = new Date();
        let orderID = cartIdRef.current;

        const productIds = Array.isArray(products)
          ? products.map((p) => p.id)
          : products.bundle?.products
          ? products.bundle.products.map((p) => p.id)
          : [products.id];
        const productsFromBackend = (await eva.getProductsFromBackendId(productIds)).Products ?? [];

        if (productsFromBackend.length === 0) {
          return;
        }

        const [firstProduct, ...otherProducts] = productsFromBackend;

        try {
          if (orderID) {
            await shoppingCartModify({
              OrderID: orderID,
              ProductID: firstProduct.product_id,
              QuantityOrdered: 1,
              LineActionType: 0,
              Children: [],
            });
          } else {
            const newCart = await shoppingCartAdd({
              orderType: 0,
              payload: {
                ProductID: firstProduct.product_id,
                QuantityOrdered: 1,
              },
            });

            orderID = newCart?.OrderID;
          }
        } catch (err) {
          Logger.instance.error(`Could not add Revieve products:`, err);
        }

        if (otherProducts.length > 0) {
          try {
            await Promise.all(
              otherProducts.map(async (product, idx) => {
                if (orderID) {
                  return shoppingCartModify({
                    OrderID: orderID,
                    ProductID: product.product_id,
                    QuantityOrdered: 1,
                    LineActionType: 0,
                    Children: [],
                  });
                } else {
                  return shoppingCartAdd({
                    orderType: 0,
                    payload: {
                      ProductID: product.product_id,
                      QuantityOrdered: 1,
                    },
                  });
                }
              })
            );
          } catch (err) {
            Logger.instance.error(`Could not add Revieve products:`, err);
          }
        }

        notifyAdded(addedAt);
        refreshCart();
      },
      onCheckout: (data: any) => {
        // User clicked the checkout button that is visible on results page
        // after adding one or more products to cart. Redirect to checkout page?
        Logger.instance.info("User clicked checkout", data);
        unmount();
        router.push("/checkout");
      },
      onClickProduct: (product: any) => {
        // To be filled in by Kiko-Skincare-PoC.
        Logger.instance.info("User clicked info action.", product);
        eva.getProductsFromBackendId([product.id]).then((data) => {
          const product = data.Products?.[0];
          const productUrl = location.origin + "/" + router.locale + "/p/" + product.slug + "-" + product.product_id;
          var win = window.open(productUrl, "_blank");
          win?.focus();
        });
      },

      onTrackingEvent: (data: { type: string; partnerId: string; payload: { name: string; payload: any } }) => {
        if (!data) return;

        const { type, payload } = data;

        if (type === "ua") return; // we don't want to handle legacy UA events

        const { name: eventName, payload: eventPayload } = payload;

        if (eventName === "restart") {
          setIsRestarted(true);
        }

        if (eventPayload.category === "System") return; // remove this line if you want to receive System events

        // custom mapping for addToCart
        if (eventName === "add_to_cart") {
          TrackingUtils.track({
            event: "revieveAddToCart",
            ecommerce: {
              currencyCode: "EUR",
              add: {
                actionField: {
                  list: "skincare",
                  action: "add",
                },
                product: {
                  name: eventPayload.item_name,
                  id: eventPayload.id,
                  price: eventPayload.price,
                  brand: eventPayload.item_manufacturer,
                  category: eventPayload.item_category,
                  variant: "",
                  quantity: 1,
                },
              },
            },
          });
        }
        // custom mapping for page_view
        if (eventName === "page_view") {
          TrackingUtils.track({
            event: "revievePageview",
            virtualPageURL: eventPayload.page_location,
            virtualPageTitle: eventPayload.page_title,
          });
        }

        // default mapping
        TrackingUtils.track({
          event: "revieveEvent",
          name: eventName,
          payload: eventPayload,
        });
      },
    };
  })();

  return (
    <Button
      variant={props.variant}
      className={classNames(props.entry ? styles.spacedButton : styles.bigButton, props.className)}
      onClick={() => onTriggerClick(props.productId)}
      disabled={hasProductError}
      {...inspectorMode?.getProps("buttonText")}
    >
      <EscapeNewLine text={buttonText!} />
    </Button>
  );
}
